// Chart module for "Show Unique Listeners Over Time"
// Owns the ApexCharts instance and data fetching/updating.

export function createUniqueListenersOverTimeChart(el, apiUrl) {
  if (!el) throw new Error('unique_listeners over_time_chart: element not found');
  const endpoint = apiUrl || '/analytics/chart';

  let apexChart = null;

  function filenameForPreset(days) {
    const d = String(days).replace(/\D/g, '') || 'custom';
    return `listeners_over_time_${d}_range`;
  }

  function filenameForCustom(from, to) {
    return `listeners_over_time_custom_range_${from}_to_${to}`;
  }

  function parseSeries(data) {
    // Accepts several shapes and returns [[x,y], ...]
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.series) && Array.isArray(data.labels)) {
      if (data.series.length === data.labels.length) {
        return data.labels.map((lab, i) => [lab, data.series[i]]);
      }
    }
    if (data && Array.isArray(data.series)) return data.series;
    if (data && Array.isArray(data.chart_data)) {
      return data.chart_data.map(r => [r.created_at_date || r.date || '', r.unique_listeners || r.total || 0]);
    }
    if (data && Array.isArray(data.download_data)) {
      return data.download_data.map(r => [r.created_at_date || r.date || '', r.unique_listeners || r.total || 0]);
    }
    return [];
  }

  async function load(params, filename) {
    const url = `${endpoint}?${params.toString()}`;
    const res = await fetch(url, { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
    if (!res.ok) throw new Error(`Fetch failed ${res.status}`);
    const data = await res.json();
    const seriesData = parseSeries(data);

    const options = {
      series: [{ name: 'Unique Listeners', data: seriesData }],
      colors: ["#1C7EBD"],
      fill: { type: "solid", colors: ["#C9E6F7"] },
      chart: {
        height: 300,
        width: "100%",
        type: "area",
        id: "uniqueListenersChartID",
        fontFamily: "manrope",
        toolbar: {
          export: {
            csv: {
              filename,
              headerCategory: 'Date',
              categoryFormatter: (val) => {
                const date = new Date(val);
                return date.toISOString().split('T')[0];
              }
            },
            svg: { filename },
            png: { filename }
          }
        }
      },
      dataLabels: { enabled: false },
      xaxis: { type: 'datetime' },
      stroke: { curve: 'smooth' },
      tooltip: { shared: true, x: { format: "dd MMM yyyy" } }
    };

    if (apexChart && typeof apexChart.updateOptions === 'function') {
      await apexChart.updateOptions({
        series: options.series,
        chart: { toolbar: options.chart.toolbar }
      }, false, true);
    } else {
      try { if (apexChart && typeof apexChart.destroy === 'function') apexChart.destroy(); } catch (_) {}
      apexChart = new ApexCharts(el, options);
      await apexChart.render();
    }

    return seriesData;
  }

  return {
    async loadPreset(days = 7) {
      const d = Number(days) || 7;
      const params = new URLSearchParams();
      params.append('chart_type', 'listeners_over_time');
      params.append('date_range', `${d}d`);
      return load(params, filenameForPreset(d));
    },
    async loadCustom(from, to) {
      const params = new URLSearchParams();
      params.append('chart_type', 'listeners_over_time');
      params.append('date_range', 'custom');
      params.append('start_date', from);
      params.append('end_date', to);
      return load(params, filenameForCustom(from, to));
    },
    destroy() {
      try { if (apexChart && typeof apexChart.destroy === 'function') apexChart.destroy(); } catch (_) {}
      apexChart = null;
    }
  };
}

